<template>
  <!-- MBR083 Invite Friends — member_app_UI/song_fa_members_invite_friends_mobile -->
  <div class="page-header align-items-start min-vh-100 mbr-invite">
    <span class="mask bg-white"></span>
    <div class="container p-0">
      <header class="mbr-invite__bar">
        <button type="button" class="mbr-invite__back" :aria-label="$t('common.back')" @click="goBack()">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="m15 18-6-6 6-6" />
          </svg>
        </button>
        <h1 class="mbr-invite__title">{{ $t('invite.title') }}</h1>
      </header>

      <main class="mbr-invite__body pb-90">
        <!-- MBR084 hero. The design's "Earn S$5" pill is dropped: the member API has no reward amount. -->
        <section class="mbr-invite__hero">
          <span class="mbr-invite__hero-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <polyline points="20 12 20 22 14 22 14 12" />
              <rect x="12" y="7" width="10" height="5" rx="1" />
              <line x1="17" y1="22" x2="17" y2="7" />
            </svg>
          </span>
          <!-- <p>, not <h2>: Material Dashboard sizes headings -->
          <p class="mbr-invite__hero-title">{{ $t('invite.hero_title') }}</p>
          <p class="mbr-invite__hero-text">
            {{ $t('invite.hero_text') }}
          </p>
        </section>

        <!-- MBR084 how it works -->
        <section class="mbr-invite__card">
          <p class="mbr-invite__card-title">{{ $t('invite.how_it_works') }}</p>
          <ol class="mbr-invite__steps">
            <li v-for="(step, index) in steps" :key="index" class="mbr-invite__step">
              <span class="mbr-invite__step-no">{{ index + 1 }}</span>
              <div class="mbr-invite__step-copy">
                <p class="mbr-invite__step-title">{{ step.title }}</p>
                <p class="mbr-invite__step-text">{{ step.text }}</p>
              </div>
            </li>
          </ol>
        </section>

        <!-- MBR084 invite link + Copy -->
        <section>
          <label for="inviteUrl" class="mbr-invite__eyebrow">{{ $t('invite.your_link') }}</label>
          <div class="mbr-invite__link">
            <input
              ref="inviteInput"
              id="inviteUrl"
              type="text"
              class="mbr-invite__link-input"
              :value="inviteUrl"
              readonly
              @focus="$event.target.select()"
            />
            <button
              type="button"
              class="mbr-invite__copy"
              :disabled="!inviteUrl"
              @click="copyURL"
            >
              {{ copied ? $t('invite.copied') : $t('invite.copy') }}
            </button>
          </div>
        </section>

        <!-- MBR084 share -->
        <section class="mbr-invite__share">
          <button type="button" class="mbr-invite__cta" :disabled="!inviteUrl" @click="shareLink">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
              <polyline points="16 6 12 2 8 6" />
              <line x1="12" y1="2" x2="12" y2="15" />
            </svg>
            <span>{{ $t('invite.share_link') }}</span>
          </button>

          <div class="mbr-invite__apps">
            <a
              class="mbr-invite__app mbr-invite__app--whatsapp"
              :class="{ 'is-disabled': !inviteUrl }"
              :href="whatsappUrl"
              target="_blank"
              rel="noopener"
              :aria-label="$t('invite.share_whatsapp')"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
              </svg>
            </a>
            <a
              class="mbr-invite__app mbr-invite__app--telegram"
              :class="{ 'is-disabled': !inviteUrl }"
              :href="telegramUrl"
              target="_blank"
              rel="noopener"
              :aria-label="$t('invite.share_telegram')"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </a>
            <a
              class="mbr-invite__app mbr-invite__app--sms"
              :class="{ 'is-disabled': !inviteUrl }"
              :href="smsUrl"
              :aria-label="$t('invite.share_message')"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </a>
            <button
              type="button"
              class="mbr-invite__app mbr-invite__app--more"
              :disabled="!inviteUrl"
              :aria-label="$t('invite.share_more')"
              @click="shareLink"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <circle cx="12" cy="12" r="1" />
                <circle cx="19" cy="12" r="1" />
                <circle cx="5" cy="12" r="1" />
              </svg>
            </button>
          </div>
        </section>
      </main>
    </div>
  </div>
</template>

<script>
import { mapMutations } from "vuex";
import MemberService from "../services/member.service";
import "bootstrap/dist/js/bootstrap.bundle";
import { getMerchantCode } from "@/config/utils";

export default {
  name: "invite-friends",
  data() {
    return {
      inviteUrl: "",
      merchantCode: "",
      memberInfo: {},
      copied: false,
      copiedTimer: null,
    };
  },
  computed: {
    // computed (not data) so the copy follows the selected language
    steps() {
      return [
        { title: this.$t("invite.step1_title"), text: this.$t("invite.step1_text") },
        { title: this.$t("invite.step2_title"), text: this.$t("invite.step2_text") },
        { title: this.$t("invite.step3_title"), text: this.$t("invite.step3_text") },
      ];
    },
    shareText() {
      return this.$t("invite.share_text");
    },
    shareMessage() {
      return `${this.shareText} ${this.inviteUrl}`;
    },
    whatsappUrl() {
      return this.inviteUrl ? `https://wa.me/?text=${encodeURIComponent(this.shareMessage)}` : null;
    },
    telegramUrl() {
      return this.inviteUrl
        ? `https://t.me/share/url?url=${encodeURIComponent(this.inviteUrl)}&text=${encodeURIComponent(this.shareText)}`
        : null;
    },
    smsUrl() {
      // `?&body=` is read by both iOS and Android messaging apps
      return this.inviteUrl ? `sms:?&body=${encodeURIComponent(this.shareMessage)}` : null;
    },
  },
  // draws its own header like the design: hide the top navbar, and keep the
  // sidenav as an off-screen drawer (same as Donation)
  beforeMount() {
    this.toggleNavbarOnly();
    this.toggleHideConfig();
    const app = document.querySelector("#app");
    app.classList.add("mbr-sidenav-overlay");
    app.classList.remove("g-sidenav-pinned");
  },
  mounted() {
    const code = getMerchantCode();
    this.merchantCode = code ? `/${code}` : "";
    this.getMemberInfo();
  },
  beforeUnmount() {
    clearTimeout(this.copiedTimer);
    this.toggleNavbarOnly();
    this.toggleHideConfig();
    const app = document.querySelector("#app");
    app.classList.remove("mbr-sidenav-overlay");
    if (window.innerWidth > 1200) {
      app.classList.add("g-sidenav-pinned");
    }
  },
  methods: {
    ...mapMutations(["toggleNavbarOnly", "toggleHideConfig"]),
    getMemberInfo() {
      MemberService.getProfile().then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.memberInfo = response.data.data;
            this.inviteUrl = `${
              window.location.origin + this.merchantCode
            }/sign-in/${this.memberInfo.my_referral_code}`;
          }
        },
        (error) => {
          console.log(error);
        }
      );
    },
    goBack() {
      this.$router.push(this.merchantCode + "/account");
    },
    async copyURL() {
      if (!this.inviteUrl) return;
      try {
        await navigator.clipboard.writeText(this.inviteUrl);
      } catch (e) {
        // Clipboard API needs a secure context; fall back to the legacy path
        const input = this.$refs.inviteInput;
        input.select();
        document.execCommand("copy");
        input.blur();
      }
      this.copied = true;
      clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => {
        this.copied = false;
      }, 2000);
    },
    async shareLink() {
      if (!this.inviteUrl) return;
      if (navigator.share) {
        try {
          await navigator.share({ text: this.shareText, url: this.inviteUrl });
        } catch (e) {
          // user dismissed the share sheet
        }
        return;
      }
      this.copyURL();
    },
  },
};
</script>
<style scoped>
/* MBR083 Invite Friends. The old block was unscoped (`.item-content h4`, `.avatar-invite`,
   `#invite-friends p`); nothing else in src/ uses those selectors. */
.mbr-invite.page-header {
  overflow: visible;
  overflow-x: clip;
  font-family: var(--mbr-font-body);
  color: var(--mbr-color-ink-headline);
}

/* custom.css still carries the legacy `* { font-family: Poppins }` */
.mbr-invite :where(:not(.material-icons-round)) {
  font-family: var(--mbr-font-body);
}

.mbr-invite .mask {
  background: var(--mbr-color-canvas) !important;
  opacity: 1;
}

.mbr-invite .container {
  max-width: 440px;
  margin: 0 auto;
}

.mbr-invite p {
  margin: 0;
}

/* header */
.mbr-invite__bar {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 24px 12px;
  background: var(--mbr-nav-bg);
  backdrop-filter: blur(var(--mbr-nav-blur));
  -webkit-backdrop-filter: blur(var(--mbr-nav-blur));
}

.mbr-invite__back {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: var(--mbr-color-surface);
  border: 1px solid var(--mbr-color-outline);
  border-radius: var(--mbr-radius-full);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
  color: var(--mbr-color-ink-headline);
  transition: background-color 0.15s ease;
}

.mbr-invite__back:hover {
  background: var(--mbr-color-tint);
}

.mbr-invite__back svg {
  width: 18px;
  height: 18px;
}

.mbr-invite__title {
  margin: 0;
  font-family: var(--mbr-font-headline) !important;
  font-size: 24px;
  font-weight: 800;
  line-height: 32px;
  letter-spacing: -0.02em;
  color: var(--mbr-color-ink-headline);
}

.mbr-invite__body {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 8px 20px 32px;
}

/* hero */
.mbr-invite__hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  text-align: center;
  background: rgba(var(--mbr-color-palepink-rgb), 0.45);
  border: 1px solid var(--mbr-color-palepink);
  border-radius: var(--mbr-radius-xl);
}

.mbr-invite__hero-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin-bottom: 14px;
  background: rgba(var(--mbr-color-surface-rgb), 0.8);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.5);
  border-radius: var(--mbr-radius-full);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
  color: var(--mbr-color-primary);
}

.mbr-invite__hero-icon svg {
  width: 32px;
  height: 32px;
}

.mbr-invite__hero-title {
  font-family: var(--mbr-font-headline) !important;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.25;
  color: var(--mbr-color-ink-strong);
}

.mbr-invite__hero-text {
  max-width: 270px;
  margin-top: 8px !important;
  font-size: 12px;
  line-height: 1.625;
  color: var(--mbr-color-ink-secondary);
}

/* how it works */
.mbr-invite__card {
  padding: 20px;
  background: var(--mbr-color-card);
  border: 1px solid var(--mbr-color-tint-dark);
  border-radius: var(--mbr-radius-xl);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
}

.mbr-invite__card-title {
  margin-bottom: 16px !important;
  font-family: var(--mbr-font-headline) !important;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: var(--mbr-color-ink-headline);
}

.mbr-invite__steps {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;
}

/* connecting line behind the numbered circles */
.mbr-invite__steps::before {
  content: "";
  position: absolute;
  top: 16px;
  bottom: 16px;
  left: 15px;
  width: 2px;
  background: var(--mbr-color-tint-dark);
}

.mbr-invite__step {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.mbr-invite__step-no {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: var(--mbr-color-primary);
  border-radius: var(--mbr-radius-full);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
  font-family: var(--mbr-font-headline) !important;
  font-size: 12px;
  font-weight: 700;
  color: var(--mbr-color-on-primary);
}

.mbr-invite__step-copy {
  padding-top: 4px;
}

.mbr-invite__step-title {
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: var(--mbr-color-ink-headline);
}

.mbr-invite__step-text {
  margin-top: 2px !important;
  font-size: 12px;
  line-height: 16px;
  color: var(--mbr-color-ink-secondary);
}

/* invite link */
.mbr-invite__eyebrow {
  display: block;
  margin: 0 0 8px;
  padding: 0 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--mbr-color-ink-soft);
}

.mbr-invite__link {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 56px;
  padding: 0 8px 0 16px;
  background: var(--mbr-color-tint);
  border: 1px solid var(--mbr-color-outline);
  border-radius: var(--mbr-radius-full);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
}

.mbr-invite__link-input {
  flex: 1;
  min-width: 0;
  padding: 0;
  background: transparent;
  border: 0;
  outline: 0;
  font-size: 12px;
  font-weight: 600;
  color: var(--mbr-color-ink-headline);
  text-overflow: ellipsis;
}

.mbr-invite__copy {
  flex: none;
  height: 40px;
  padding: 0 16px;
  background: var(--mbr-color-primary);
  border: 0;
  border-radius: var(--mbr-radius-full);
  font-size: 12px;
  font-weight: 700;
  color: var(--mbr-color-on-primary);
  transition: background-color 0.15s ease;
}

.mbr-invite__copy:hover:not(:disabled),
.mbr-invite__cta:hover:not(:disabled) {
  background: var(--mbr-color-primary-light);
}

/* share */
.mbr-invite__share {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 4px;
}

.mbr-invite__cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 56px;
  background: var(--mbr-color-primary);
  border: 0;
  border-radius: var(--mbr-radius-full);
  box-shadow: 0 8px 20px rgba(var(--mbr-color-primary-rgb), 0.25);
  font-family: var(--mbr-font-headline) !important;
  font-size: 16px;
  font-weight: 800;
  color: var(--mbr-color-on-primary);
  transition: background-color 0.15s ease;
}

.mbr-invite__cta svg {
  width: 18px;
  height: 18px;
}

.mbr-invite__copy:disabled,
.mbr-invite__cta:disabled {
  opacity: 0.5;
}

.mbr-invite__apps {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding-top: 8px;
}

.mbr-invite__app {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  background: var(--mbr-color-surface);
  border: 1px solid var(--mbr-color-outline);
  border-radius: var(--mbr-radius-full);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
  transition: background-color 0.15s ease;
}

.mbr-invite__app:hover {
  background: var(--mbr-color-tint);
}

.mbr-invite__app svg {
  width: 22px;
  height: 22px;
}

.mbr-invite__app--sms svg,
.mbr-invite__app--more svg {
  width: 20px;
  height: 20px;
}

.mbr-invite__app--whatsapp {
  color: #25d366;
}

.mbr-invite__app--telegram {
  color: #0088cc;
}

.mbr-invite__app--sms {
  color: var(--mbr-color-primary);
}

.mbr-invite__app--more {
  color: var(--mbr-color-ink-secondary);
}

.mbr-invite__app.is-disabled,
.mbr-invite__app:disabled {
  opacity: 0.5;
  pointer-events: none;
}
</style>
